export enum ResetPattern {
  Never = 0,
  Hourly = 1,
  Daily = 2,
  Weekly = 3,
  Monthly = 4,
  Quarterly = 5,
  Yearly = 6,
}

export const ResetPatternKeys = {
  [ResetPattern.Never]: 'form-builder:form-properties.custom-identifier.reset-pattern.never',
  [ResetPattern.Hourly]: 'form-builder:form-properties.custom-identifier.reset-pattern.hourly',
  [ResetPattern.Daily]: 'form-builder:form-properties.custom-identifier.reset-pattern.daily',
  [ResetPattern.Weekly]: 'form-builder:form-properties.custom-identifier.reset-pattern.weekly',
  [ResetPattern.Monthly]: 'form-builder:form-properties.custom-identifier.reset-pattern.monthly',
  [ResetPattern.Quarterly]: 'form-builder:form-properties.custom-identifier.reset-pattern.quarterly',
  [ResetPattern.Yearly]: 'form-builder:form-properties.custom-identifier.reset-pattern.yearly',
} as const;

export type SourceType = 'TemplateForm';

export type CustomIdentifier = {
  sourceId: string;
  sourceType: SourceType;
  format: string;
  seedValue: number;
  padding: number;
  resetPattern: ResetPattern;

  // If true, the changes will apply to all existing versions of the source (e.g. all existing client forms versions of the form template)
  // If false, the changes will only apply to new versions.
  applyToHistoricalData: boolean;

  // FE only
  enabled: boolean;
};

export interface CustomIdentifierResponse extends CustomIdentifier {
  id: string;
  lastIssuedUtc: string;
  lastNumber?: number;
  createdUtc: string;
  modifiedUtc: string;
}

export const DefaultCustomIdentifier: CustomIdentifierResponse = {
  seedValue: 1,
  format: 'Prefix-{YYYY}-{X}',
  id: '',
  lastIssuedUtc: '',
  createdUtc: '',
  modifiedUtc: '',
  padding: 3,
  resetPattern: ResetPattern.Never,
  sourceId: '',
  sourceType: 'TemplateForm',
  applyToHistoricalData: false,
  enabled: false,
};

export const CUIDDateOptions = [
  { id: 'year', options: ['YYYY', 'YY'] },
  { id: 'quarter', options: ['Q'] },
  { id: 'month', options: ['MM', 'M'] },
  { id: 'week', options: ['WW', 'W'] },
  { id: 'day', options: ['DD', 'D'] },
] as const;

export const CUIDTimeOptions = [
  { id: 'hours', options: ['hh', 'h'] },
  { id: 'minutes', options: ['mm', 'm'] },
  { id: 'seconds', options: ['ss', 's'] },
] as const;

export const CUIDCounter = 'X';

export const CUIDDateValues = CUIDDateOptions.flatMap((x) => x.options);
export const CUIDTimeValues = CUIDTimeOptions.flatMap((x) => x.options);

export const CUIDDateTimeOptions = [...CUIDDateOptions, ...CUIDTimeOptions].flatMap((x) => x.options);

export const CUIDOptions = [...CUIDDateTimeOptions, CUIDCounter] as const;

export const CUIDOptionRegex = new RegExp(`{(${CUIDOptions.join('|')})}`, 'g');

export const CUIDOptionRank: Record<(typeof CUIDOptions)[number], ResetPattern | -1> = {
  YYYY: ResetPattern.Yearly,
  YY: ResetPattern.Yearly,
  Q: ResetPattern.Quarterly,
  MM: ResetPattern.Monthly,
  M: ResetPattern.Monthly,
  WW: ResetPattern.Weekly,
  W: ResetPattern.Weekly,
  DD: ResetPattern.Daily,
  D: ResetPattern.Daily,
  hh: ResetPattern.Hourly,
  h: ResetPattern.Hourly,
  mm: -1,
  m: -1,
  ss: -1,
  s: -1,
  X: -1,
};

export const requiredTagsByPattern: Record<ResetPattern, ResetPattern[]> = {
  [ResetPattern.Never]: [],
  [ResetPattern.Hourly]: [ResetPattern.Hourly, ResetPattern.Daily, ResetPattern.Monthly, ResetPattern.Yearly],
  [ResetPattern.Daily]: [ResetPattern.Daily, ResetPattern.Monthly, ResetPattern.Yearly],
  [ResetPattern.Weekly]: [ResetPattern.Weekly, ResetPattern.Yearly],
  [ResetPattern.Monthly]: [ResetPattern.Monthly, ResetPattern.Yearly],
  [ResetPattern.Quarterly]: [ResetPattern.Quarterly, ResetPattern.Yearly],
  [ResetPattern.Yearly]: [ResetPattern.Yearly],
};
